import React, {useContext} from 'react'
import {Link, useMatch} from 'react-router-dom'
import {useTranslation} from 'react-i18next'
import ReactTooltip from 'react-tooltip'
import {FacilityFeature} from '@common/models'

import {Pusher, StyledSideNav, SideNavItem} from './SideNav.styled'
import {PractitionerNavSection} from './PractitionerNavSection'
import {PartnerAdminNavSection} from './PartnerAdminNavSection'

import {Avatar} from '~/components/general/avatar/Avatar'
import {Icon} from '~/components/general/icon/Icon'
import {PractitionerContext} from '~/components/profile/PractitionerContext'
import {PartnerAdministratorContext} from '~/components/profile/PartnerAdministratorContext'
import {useNavigationType} from '~/hooks/useNavigationType'

type SideNavProps = {
  hideButtons?: boolean
}

export const SideNav: React.FC<SideNavProps> = ({hideButtons}) => {
  const {t} = useTranslation<string>()
  const {useCombinedNav, usePartnerAdminNav} = useNavigationType()
  const showPartnerAdminSection = useCombinedNav || usePartnerAdminNav
  const showPractitionerSection = useCombinedNav || !usePartnerAdminNav

  // All hooks must be called at the top level
  const patientsMatch = useMatch('/app/patients/*')
  const profileMatch = useMatch('/app/profile/*')
  const materialsMatch = useMatch('/app/materials/*')
  const questionnairesMatch = useMatch('/app/questionnaires/*')
  const screeningMatch = useMatch('/app/appointments/*')
  const partnerProfileMatch = useMatch('/app/partner-profile/*')
  const partnerMatch = useMatch('/app/partner/*')
  const reportsMatch = useMatch('/app/reports/*')

  const isActive = {
    patients: !!patientsMatch,
    profile: !!profileMatch,
    materials: !!materialsMatch,
    questionnaires: !!questionnairesMatch,
    screening: !!screeningMatch,
    partnerProfile: !!(partnerProfileMatch || partnerMatch),
    reports: !!reportsMatch,
  }
  const {practitioner, selectedEmployment} = useContext(PractitionerContext)
  const {partnerAdministrator} = useContext(PartnerAdministratorContext)
  const hasScreening = Boolean(
    selectedEmployment?.features?.includes(FacilityFeature.SCREENING),
  )

  return (
    <StyledSideNav>
      <Link to="/">
        <SideNavItem logo>
          <Icon name="Logo" size="xl" />
        </SideNavItem>
      </Link>
      {!hideButtons && (
        <>
          {showPractitionerSection && (
            <PractitionerNavSection
              isActive={{
                patients: isActive.patients,
                materials: isActive.materials,
                questionnaires: isActive.questionnaires,
                screening: isActive.screening,
                reports: isActive.reports,
              }}
              showAppointments={hasScreening}
              showReports={hasScreening && !showPartnerAdminSection}
            />
          )}

          {showPartnerAdminSection && (
            <PartnerAdminNavSection active={isActive.partnerProfile} />
          )}

          <Pusher />

          {(showPractitionerSection || showPartnerAdminSection) && (
            <Link to="/app/profile" data-tip={t('Profile')} data-for="sideNav">
              <SideNavItem active={isActive.profile}>
                <Avatar
                  size="sm"
                  name={
                    showPractitionerSection
                      ? practitioner
                      : partnerAdministrator
                  }
                />
              </SideNavItem>
            </Link>
          )}
        </>
      )}
      <ReactTooltip id="sideNav" place="right" effect="solid" />
    </StyledSideNav>
  )
}
